import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOptroConnection } from "./useOptroConnection";

export const CONTROL_OPTRO_IMPORT_PATH = "/audit/controls/v2/import/optro";
export const WORK_STEP_OPTRO_IMPORT_PATH = "/audit/work-steps/import/optro";

interface OptroImportLauncherOptions {
  // Which import wizard to open — one of the *_OPTRO_IMPORT_PATH constants.
  wizardPath: string;
  // Pass false when the entry point is hidden (e.g. bulk-import flag off) so
  // no integrations request fires for it.
  enabled?: boolean;
}

// Launches an Optro import wizard from an entry point (welcome state / create
// chooser). If the workspace isn't connected to Optro yet, opens the Configure
// Optro dialog in place — overlaying the current page — and only navigates into
// the wizard once connected. While the connection check is still loading we
// navigate optimistically; the wizard's own gate covers the rare unconnected
// case.
export function useOptroImportLauncher({
  wizardPath,
  enabled = true,
}: OptroImportLauncherOptions) {
  const navigate = useNavigate();
  const { connected, loading } = useOptroConnection(enabled);
  const [dialogOpen, setDialogOpen] = useState(false);

  const launchImport = () => {
    if (loading || connected) {
      navigate(wizardPath);
    } else {
      setDialogOpen(true);
    }
  };

  const onConnected = () => {
    setDialogOpen(false);
    navigate(wizardPath);
  };

  return {
    launchImport,
    dialogProps: {
      open: dialogOpen,
      onOpenChange: setDialogOpen,
      onConnected,
    },
  };
}
